/**
 * Zustand store for managing the Configurations page UI state.
 */

import { create } from 'zustand';

interface ConfigurationStore {
  // Tab
  activeConfigTab: 'strategies' | 'aggregations';
  setActiveConfigTab: (tab: 'strategies' | 'aggregations') => void;

  // Strategy selection (new: user_strategies level)
  selectedStrategyId: string | null;
  setSelectedStrategyId: (id: string | null) => void;

  // Code viewer
  codeViewOpen: boolean;
  setCodeViewOpen: (open: boolean) => void;
  /**
   * When non-null, the StrategyCodeViewer shows this code instead of the
   * current strategy's code. Set to a historical version's code when the user
   * clicks "Show code" while a historical version is selected in the detail panel.
   * Reset to null when the strategy changes or the viewer closes.
   */
  codeViewVersionCode: string | null;
  setCodeViewVersionCode: (code: string | null) => void;

  // Config selection (within a strategy)
  selectedConfigId: string | null;
  setSelectedConfigId: (id: string | null) => void;

  // Detail tabs
  activeDetailTab: 'runs' | 'paper' | 'versions';
  setActiveDetailTab: (tab: 'runs' | 'paper' | 'versions') => void;

  // Filters
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  filterStrategy: string;
  setFilterStrategy: (s: string) => void;
  filterSymbol: string;
  setFilterSymbol: (s: string) => void;
  /**
   * Reset every field to its default. Called on logout/login (SHA-71) so a
   * previously-selected strategy/config id or its viewed code doesn't leak
   * into the next account's session.
   */
  reset: () => void;
}

const defaultConfigurationState = {
  activeConfigTab: 'strategies' as const,
  selectedStrategyId: null as string | null,
  codeViewOpen: false,
  codeViewVersionCode: null as string | null,
  selectedConfigId: null as string | null,
  activeDetailTab: 'runs' as const,
  searchQuery: '',
  filterStrategy: '',
  filterSymbol: '',
};

export const useConfigurationStore = create<ConfigurationStore>((set) => ({
  ...defaultConfigurationState,
  setActiveConfigTab: (tab) => set({ activeConfigTab: tab }),
  setSelectedStrategyId: (id) => set({ selectedStrategyId: id, selectedConfigId: null, codeViewOpen: false, codeViewVersionCode: null }),
  setCodeViewOpen: (open) => set(open ? { codeViewOpen: true } : { codeViewOpen: false, codeViewVersionCode: null }),
  setCodeViewVersionCode: (code) => set({ codeViewVersionCode: code }),
  setSelectedConfigId: (id) => set({ selectedConfigId: id, codeViewOpen: false, codeViewVersionCode: null }),
  setActiveDetailTab: (tab) => set({ activeDetailTab: tab }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setFilterStrategy: (s) => set({ filterStrategy: s }),
  setFilterSymbol: (s) => set({ filterSymbol: s }),
  reset: () => set(defaultConfigurationState),
}));
